/**
 * documentRepositoryTopics.js — historia 170 (Repositorio Documental). PURE leaf, no imports, no
 * React, no API calls. Two responsibilities only, both data-only lookups:
 *
 *   1. `topicLabelKey(topic)` — the 28-value `DocumentTopic` catalogue (03-dominio.md §"Etiquetas de
 *      temática") -> the i18n key to render. A topic absent from the map (a future product topic,
 *      RN2/RN3) falls back to its own literal, rendered as-is — NEVER hidden.
 *   2. `navigationWindowFor(windowKey)` — the 12-value closed `window.key` catalogue (03-dominio.md
 *      §"Mapa de navegación") -> `{ path, permissionCode }`. Consumed starting J4 (CU8): Jupiter
 *      translates `navigation.window.key` into a route and asks the existing menu guard
 *      (`isCurrentPathAllowed`) whether it is authorized. An unknown key resolves to `{}` (CLAUDE.md:
 *      no `undefined` in business logic — CU8 A2, RN33: no destination, no guess).
 *
 * ⚠️ This module NEVER decides visibility (S155): whether a topic/row/action is shown depends solely
 * on what Sun returns (`topics[].permissions`, `row.permissions`, `row.navigation`), never on a
 * client-side allow-list. It only maps identifiers Sun already sent to i18n keys and window routes.
 */

const TOPIC_LABEL_KEYS = {
  PART: 'Part',
  PART_REFERENCE: 'Part reference',
  FEATURE_TASK: 'Feature',
  FEATURE_GROUP: 'Feature group',
  TOOL_REFERENCE: 'Tool reference',
  TOOL: 'Tool',
  FABRICATION_ORDER: 'Fabrication order',
  DEVICE: 'Asset',
  WORKFLOW: 'Workflow',
  JOB: 'Job',
  TASK: 'Task',
  DEVICE_GROUP: 'Asset group',
  RAW_MATERIAL: 'Raw material',
  FIXTURE: 'Fixture',
  FIXTURE_ASSEMBLY: 'Fixture assembly',
  ORDER: 'Order',
  ORDER_LINE: 'Line order',
  DELIVERY_NOTE: 'Delivery note',
  PURCHASE_ORDER_THEORETICAL_RECEPTION: 'Theoretical reception',
  PURCHASE_ORDER_REAL_RECEPTION: 'Real reception',
  PURCHASE_ORDER_QUALITATIVE_RECEPTION: 'Qualitative reception',
  MATERIAL_RECEPTION_INCIDENCE: 'Material reception incidence',
  QUALITY_INCIDENCE: 'Quality incidence',
  QUALITY_INCIDENCE_ACTION: 'Quality incidence action',
  INSPECTION_REPORT_FEATURE: 'Inspection report feature',
  RAW_MATERIAL_REFERENCE: 'Raw material reference',
  FIXTURE_REFERENCE: 'Fixture reference',
  FIXTURE_ASSEMBLY_REFERENCE: 'Fixture assembly reference'
}

/** RN2/RN3 — a topic with no entry in the map shows its own literal, never hidden. */
export const topicLabelKey = (topic) => TOPIC_LABEL_KEYS[topic] || topic

/** The 28 topics this build knows about (RN2 guard: every one must resolve to SOME label). */
export const allDocumentTopics = () => Object.keys(TOPIC_LABEL_KEYS)

// window.key -> { path, permissionCode }. 03-dominio.md §"Mapa de navegación" (G1-3, G1-4, G1-5).
const NAVIGATION_WINDOWS = {
  PART_REFERENCE: { path: '/resources/reference', permissionCode: 'PART' },
  FABRICATION_ORDER: { path: '/fabricationOrders', permissionCode: 'FABRICATION ORDERS' },
  ORDER: { path: '/orders', permissionCode: 'ORDERS TAB' },
  WORKFLOW: { path: '/process/workflow2', permissionCode: 'WORKFLOWS_MANAGER' },
  TOOL_REFERENCE: { path: '/resources/toolManager', permissionCode: 'TOOLS' },
  RAW_MATERIAL_REFERENCE: { path: '/resources/rawmaterialReference', permissionCode: 'RAW MATERIAL' },
  FIXTURE_REFERENCE: { path: '/resources/fixtureReference', permissionCode: 'FIXTURE' },
  FIXTURE_ASSEMBLY_REFERENCE: { path: '/resources/fixtureAssemblyReference', permissionCode: 'RESOURCES' },
  DEVICE: { path: '/configuration/assets', permissionCode: 'ASSETS' },
  DEVICE_GROUP: { path: '/configuration/assetGroups', permissionCode: 'CONFIGURATION' },
  QUALITY_INCIDENCE: { path: '/quality', permissionCode: 'QUALITY' },
  INSPECTION_REPORT: { path: '/dataAnalytics/reports/inspection', permissionCode: 'INSPECTION' }
}

export const ALL_NAVIGATION_WINDOW_KEYS = Object.keys(NAVIGATION_WINDOWS)

/** CU8 step 1 (J4): `navigation.window.key` -> route + guard permission. `{}` = no destination. */
export const navigationWindowFor = (windowKey) => NAVIGATION_WINDOWS[windowKey] || {}
